import { supabase } from '@/lib/supabase';

const API_BASE_URL = '/api';

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface RequestOptions {
  method?: RequestMethod;
  body?: any;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  status: number;
  
  
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}


async function apiRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  // Get Supabase access token
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    interface ErrorResponse {
      message: string;
    }
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' })) as ErrorResponse;
    throw new ApiError(errorData.message || 'API request failed', response.status);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return {} as T;
}

export const api = {
  get: <T>(endpoint: string, headers?: Record<string, string>) => 
    apiRequest<T>(endpoint, { headers }),
  
  post: <T>(endpoint: string, data: any, headers?: Record<string, string>) => 
    apiRequest<T>(endpoint, { method: 'POST', body: data, headers }),
  
  put: <T>(endpoint: string, data: any, headers?: Record<string, string>) => 
    apiRequest<T>(endpoint, { method: 'PUT', body: data, headers }),
  
  delete: <T>(endpoint: string, headers?: Record<string, string>) => 
    apiRequest<T>(endpoint, { method: 'DELETE', headers }),
};
